import { Feature, type QualityReportAiGravimetricComposition } from 'api';

import { convertFeatureToNumber, formatWeightFeature } from '../formatFeature/formatFeature';

export const getUBCPercentage = (features: Record<string, Feature | number>) => {
  return 1 - convertFeatureToNumber(features.features_sum_non_target);
};

export const getNonAluminiumPercentage = (features: Record<string, Feature | number>) => {
  return (
    convertFeatureToNumber(features['features_material_Other Metal']) +
    convertFeatureToNumber(features['features_material_Non-Metal'])
  );
};

export const getAluminiumPercentage = (features: Record<string, Feature | number>) => {
  return 1 - getNonAluminiumPercentage(features);
};

export const getPetBottlePercentage = (features: Record<string, Feature | number>) => {
  return 1 - convertFeatureToNumber(features.features_sum_non_target);
};

export const getClearPercentage = (features: Record<string, Feature | number>) => {
  return getPetBottlePercentage(features) - convertFeatureToNumber(features.features_sum_colours);
};

export const getNonPetBottlePercentage = (features: Record<string, Feature | number>) => {
  return (
    convertFeatureToNumber(features['features_material_Clear tray']) +
    convertFeatureToNumber(features.features_material_Film) +
    convertFeatureToNumber(features['features_material_Other Plastic']) +
    convertFeatureToNumber(features['features_material_Non Plastic']) +
    convertFeatureToNumber(features['features_material_Clear Non-Food grade'])
  );
};

const MATERIAL_WEIGHT_FEATURE_KEYS = {
  foilAndTray: ['features_material_Aluminium Foil Trays'],
  foodCan: ['features_material_Aluminium Food Can'],
  nonMetal: [
    'features_material_Rigid Plastic',
    'features_material_Film',
    'features_material_Non Metallic',
    'features_material_Fibers & Textile',
    'features_material_Small Plastic',
  ],
  otherAluminium: [
    'features_material_Aluminium Aerosol',
    'features_material_Aluminium Bottles',
    'features_material_Aluminium Caps',
    'features_material_Other Aluminium',
  ],
  otherMetal: [
    'features_material_Other Metal',
    'features_material_Vapes',
    'features_material_WEEE',
    'features_material_Lighters',
  ],
};

export const getRawWeight = (composition: QualityReportAiGravimetricComposition, keys: string[]) =>
  keys.reduce((acc, key) => acc + convertFeatureToNumber(composition[key], 'sum'), 0);

export const getFoilAndTrayRawWeight = (composition: QualityReportAiGravimetricComposition) =>
  getRawWeight(composition, MATERIAL_WEIGHT_FEATURE_KEYS.foilAndTray);

export const getFoodCansRawWeight = (composition: QualityReportAiGravimetricComposition) =>
  getRawWeight(composition, MATERIAL_WEIGHT_FEATURE_KEYS.foodCan);

export const getOtherAluminiumRawWeight = (composition: QualityReportAiGravimetricComposition) =>
  getRawWeight(composition, MATERIAL_WEIGHT_FEATURE_KEYS.otherAluminium);

export const getOtherMetalRawWeight = (composition: QualityReportAiGravimetricComposition) =>
  getRawWeight(composition, MATERIAL_WEIGHT_FEATURE_KEYS.otherMetal);

export const getNonMetalRawWeight = (composition: QualityReportAiGravimetricComposition) =>
  getRawWeight(composition, MATERIAL_WEIGHT_FEATURE_KEYS.nonMetal);

export const getNonAluminiumRawWeight = (composition: QualityReportAiGravimetricComposition) =>
  getOtherMetalRawWeight(composition) + getNonMetalRawWeight(composition);

export const getAluminiumRawWeight = (composition: QualityReportAiGravimetricComposition, totalWeight: number) =>
  totalWeight - getNonAluminiumRawWeight(composition);

export const getUBCRawWeight = (composition: QualityReportAiGravimetricComposition, totalWeight: number) =>
  getAluminiumRawWeight(composition, totalWeight) -
  getFoilAndTrayRawWeight(composition) -
  getFoodCansRawWeight(composition) -
  getOtherAluminiumRawWeight(composition);

export const getMaterialsRawWeight = (composition: QualityReportAiGravimetricComposition, totalWeight: number) => {
  const aluminiumWeight = getAluminiumRawWeight(composition, totalWeight);
  const ubcWeight = getUBCRawWeight(composition, totalWeight);
  const foilAndTrayWeight = getFoilAndTrayRawWeight(composition);
  const foodCansWeight = getFoodCansRawWeight(composition);
  const otherAluminiumWeight = getOtherAluminiumRawWeight(composition);
  const otherMetalWeight = getOtherMetalRawWeight(composition);
  const nonMetalWeight = getNonMetalRawWeight(composition);
  const nonAluminiumWeight = getNonAluminiumRawWeight(composition);

  return {
    aluminiumWeight,
    foilAndTrayWeight,
    foodCansWeight,
    nonAluminiumWeight,
    nonMetalWeight,
    otherAluminiumWeight,
    otherMetalWeight,
    ubcWeight,
  };
};

export const getMaterialsFormatWeightFeature = (
  composition: QualityReportAiGravimetricComposition,
  totalWeight: number
) => {
  const {
    aluminiumWeight,
    foilAndTrayWeight,
    foodCansWeight,
    nonAluminiumWeight,
    nonMetalWeight,
    otherAluminiumWeight,
    otherMetalWeight,
    ubcWeight,
  } = getMaterialsRawWeight(composition, totalWeight);

  return {
    aluminium: formatWeightFeature(aluminiumWeight, totalWeight),
    foilAndTray: formatWeightFeature(foilAndTrayWeight, totalWeight),
    foodCans: formatWeightFeature(foodCansWeight, totalWeight),
    nonAluminium: formatWeightFeature(nonAluminiumWeight, totalWeight),
    nonMetal: formatWeightFeature(nonMetalWeight, totalWeight),
    otherAluminium: formatWeightFeature(otherAluminiumWeight, totalWeight),
    otherMetal: formatWeightFeature(otherMetalWeight, totalWeight),
    total: formatWeightFeature(totalWeight, totalWeight),
    ubc: formatWeightFeature(ubcWeight, totalWeight),
  };
};
